const VALID_TEMPLATES = ['event', 'restaurant'];
const VALID_EFFECTS = ['none', 'outline', 'shadow', 'glow'];

const removeQuotes = (value) => {
  if (typeof value !== 'string') {
    return value;
  }

  return value.replace(/^['"]|['"]$/g, '').trim();
};

const getValidTemplateId = (templateId, theme) => {
  const candidate = removeQuotes(templateId || theme);
  return VALID_TEMPLATES.includes(candidate) ? candidate : 'event';
};

const getValidEffect = (effect, fallback = 'none') => {
  const candidate = removeQuotes(effect || fallback) || fallback;
  return VALID_EFFECTS.includes(candidate) ? candidate : fallback;
};

const getNumericString = (value, fallback) => {
  const raw = removeQuotes(value);
  const numeric = Number(raw);

  if (!Number.isFinite(numeric) || numeric <= 0) {
    return fallback;
  }

  return String(Math.round(numeric));
};

const getOpacityString = (value) => {
  const raw = removeQuotes(value);
  const numeric = Number(raw);

  if (!Number.isFinite(numeric)) {
    return '100';
  }

  return String(Math.min(100, Math.max(0, Math.round(numeric))));
};

const getColor = (value, fallback) => {
  const raw = removeQuotes(value);
  return raw || fallback;
};

export const normalizeEventData = (eventData = {}) => {
  const templateId = getValidTemplateId(eventData.templateId, eventData.theme);
  const mainTextColor = getColor(eventData.mainTextColor, '#000000');
  const mainTextEffect = getValidEffect(eventData.mainTextEffect, 'none');

  return {
    ...eventData,
    templateId,
    theme: templateId,
    backgroundImageOpacity: getOpacityString(eventData.backgroundImageOpacity),
    mainTextColor,
    mainTextEffect,
    titleTextColor: getColor(eventData.titleTextColor, mainTextColor || '#000000'),
    titleTextEffect: getValidEffect(eventData.titleTextEffect, mainTextEffect || 'none'),
    titleTextEffectColor: getColor(eventData.titleTextEffectColor, '#000000'),
    titleFontSize: getNumericString(eventData.titleFontSize, '48'),
    titleFontFamily: removeQuotes(eventData.titleFontFamily) || 'default',
    subtitleTextColor: getColor(eventData.subtitleTextColor, mainTextColor || '#000000'),
    subtitleTextEffect: getValidEffect(eventData.subtitleTextEffect, mainTextEffect || 'none'),
    subtitleTextEffectColor: getColor(eventData.subtitleTextEffectColor, '#000000'),
    subtitleFontSize: getNumericString(eventData.subtitleFontSize, '20'),
    subtitleFontFamily: removeQuotes(eventData.subtitleFontFamily) || 'default',
  };
};

export const getFontFamilyStyle = (fontFamily, fallbackFamily) => {
  const normalizedFontFamily = removeQuotes(fontFamily);
  if (!normalizedFontFamily || normalizedFontFamily === 'default') {
    return fallbackFamily;
  }

  return normalizedFontFamily;
};

export const getFontSizeStyle = (fontSize, fallback) => {
  const numeric = Number(removeQuotes(fontSize));
  if (!Number.isFinite(numeric) || numeric <= 0) {
    return `${fallback}px`;
  }

  return `${numeric}px`;
};

export const getTextEffectStyles = (effect, effectColor) => {
  const normalizedEffect = getValidEffect(effect, 'none');
  const color = getColor(effectColor, '#000000');

  switch (normalizedEffect) {
    case 'outline':
      return {
        WebkitTextStroke: `1.5px ${color}`,
        textShadow: [
          `1px 0 ${color}`,
          `-1px 0 ${color}`,
          `0 1px ${color}`,
          `0 -1px ${color}`,
          `1px 1px ${color}`,
          `-1px -1px ${color}`,
          `1px -1px ${color}`,
          `-1px 1px ${color}`,
        ].join(', '),
      };
    case 'shadow':
      return {
        textShadow: `0 4px 10px ${color}`,
      };
    case 'glow':
      return {
        textShadow: `0 0 8px ${color}, 0 0 18px ${color}, 0 0 28px ${color}`,
      };
    default:
      return {
        textShadow: 'none',
        WebkitTextStroke: '0 transparent',
      };
  }
};

export const getBackgroundOverlayOpacity = (backgroundImageOpacity) => {
  const normalized = Number(getOpacityString(backgroundImageOpacity));
  return Number((1 - normalized / 100).toFixed(2));
};
